import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import URLPreview from 'Components/UIComponent/URLPreview'

// Helper function to convert URLs to links with ellipsis class
const convertLinksToAnchors = (text) => {
  if (!text) return ''

  return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>')
}

// Helper function to determine attachment type from multiple sources
const getAttachmentType = (attachment) => {
  if (!attachment) return 'document'

  // First, check if attachment_type is already set and valid
  if (attachment.attachment_type) {
    const attType = attachment.attachment_type.toLowerCase()

    if (attType === 'image' || attType.startsWith('image')) return 'image'
    if (attType === 'video' || attType.startsWith('video')) return 'video'
    if (attType === 'document' || attType.startsWith('document')) return 'document'
  }

  // Second, check mime_type
  const mimeType = (attachment.mime_type || attachment.mime || '').toLowerCase()

  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'

  // Third, check file extension from file_name
  const fileName = (attachment.file_name || attachment.name || '').toLowerCase()
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
  const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v']

  if (imageExtensions.some((ext) => fileName.endsWith(ext))) return 'image'
  if (videoExtensions.some((ext) => fileName.endsWith(ext))) return 'video'

  // Default to document
  return 'document'
}

// Helper function to check if attachment is media (image or video)
const isMediaAttachment = (attachment) => {
  const type = getAttachmentType(attachment)

  return type === 'image' || type === 'video'
}

const PersonalChatMessage = React.memo(
  ({ msg, isOutgoing, formatTime, onAttachmentClick, onReplyClick, menuNode }) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)
    const [captionExpanded, setCaptionExpanded] = useState({}) // Track expanded state per attachment index
    const MAX_PREVIEW_CHARS = 240

    const rawText = (msg?.content || '').toString()
    const isLongText = rawText?.trim().length > MAX_PREVIEW_CHARS
    const previewText = isLongText && !expanded ? `${rawText.slice(0, MAX_PREVIEW_CHARS)?.trim()}...` : rawText

    const ts = msg.timestamp || msg.createdTimestamp || msg.created_at || null
    const userDetails = getLocalUserDetails()
    const contentWithLinks = convertLinksToAnchors(previewText || '')

    const hasTextContent = Boolean(msg?.content?.trim())
    const hasAttachments = Array.isArray(msg.attachments) && msg.attachments.length > 0
    const hasMediaAttachments = hasAttachments && msg.attachments.some((att) => isMediaAttachment(att))
    const hasAnyCaption = hasAttachments && msg.attachments.some((att) => att.caption && att.caption.trim())
    const hasOnlyMediaAttachments = hasMediaAttachments && msg.attachments.every((att) => isMediaAttachment(att))

    // Calculate if we have media without captions (for grid overlay)
    const mediaWithoutCaptions = hasAttachments ? msg.attachments.filter((att) => isMediaAttachment(att) && (!att.caption || !att.caption.trim())) : []
    const hasMediaWithoutCaptions = mediaWithoutCaptions.length > 0
    const hasOnlyMediaWithoutCaptions = hasMediaAttachments && msg.attachments.every((att) => isMediaAttachment(att) && (!att.caption || !att.caption.trim()))

    // Overlay timestamp only for grid items (media without captions) when no text content
    const shouldOverlayTimestamp = hasOnlyMediaWithoutCaptions && !hasTextContent && hasMediaWithoutCaptions

    const renderTimestamp = (extraClass = '') => (
      <span className={`time ${extraClass}`.trim()}>
        {msg.edited_at && <span className="edited-label">Edited&nbsp;</span>}
        {ts ? formatTime(ts) : ''}
      </span>
    )

    const showIconBasedOnAttachmentType = (type, attachments) => {
      const attachmentArray = Array.isArray(attachments) ? attachments : []
      const count = attachmentArray.length
      const hasAnyCaption = attachmentArray.some((att) => att.caption && att.caption.trim())
      const shouldHideCount = count > 1 && hasAnyCaption
      const remainingCountText = !shouldHideCount && count > 1 ? ` (+${count - 1})` : ''

      if (type === 'image') {
        return (
          <div>
            <i className="pi pi-camera"></i>
            <p>Photo{remainingCountText}</p>
          </div>
        )
      }

      if (type === 'document') {
        return (
          <div>
            <i className="pi pi-file-pdf"></i>
            <p>Document{remainingCountText}</p>
          </div>
        )
      }

      if (type === 'video') {
        return (
          <div>
            <i className="pi pi-video"></i>
            <p>Video{remainingCountText}</p>
          </div>
        )
      }

      return null
    }

    const renderRepliedPreview = () => {
      if (!msg?.reply_to_id) return null

      const replied = msg?.repliedMessage || {}

      const repliedUser = replied.user || replied.user_info || {}
      const repliedUserId = repliedUser.userid || replied.userid
      const isYou = repliedUserId && String(repliedUserId) === String(userDetails?.id)
      const repliedFirstName = repliedUser.first_name?.trim?.() || ''
      const repliedLastName = repliedUser.last_name?.trim?.() || ''
      const repliedName = isYou ? 'You' : `${repliedFirstName} ${repliedLastName}`.trim() || 'User'

      const attachmentType = replied.attachmentsType
      const hasAttachmentMeta = Boolean(attachmentType)

      const repliedPreviewContent = (() => {
        if (msg?.is_reply_deleted) return 'Message deleted'
        if (hasAttachmentMeta) return showIconBasedOnAttachmentType(attachmentType, replied.attachmentsCount)

        return replied.content || ''
      })()

      return (
        <div
          className={`replied-message-preview ${isOutgoing ? 'out' : 'in'}`}
          onClick={(e) => {
            e.stopPropagation()
            if (onReplyClick && msg?.reply_to_id) {
              onReplyClick(msg.reply_to_id)
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="user-details">{repliedName}</div>
          <div className="replied-message-text">{repliedPreviewContent}</div>
        </div>
      )
    }

    return (
      <div className="bubble">
        {renderRepliedPreview()}

        <div className={`${hasAttachments ? 'message-content-wrapper' : ''}`}>
          {!isOutgoing && menuNode && <div className="inline-message-options">{menuNode}</div>}

          {msg?.is_forwarded && (
            <div className="forwarded-label">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="forwarded-icon">
                <path d="m654-300-51-51 159-159-159-159 51-51 210 210-210 210Zm-510 90v-144q0-80 56-136t136-56h198L411-669l51-51 210 210-210 210-51-51 123-123H336q-50 0-85 35t-35 85v144h-72Z" />
              </svg>
              <span className="forwarded-text">{t('FORWARDED')}</span>
            </div>
          )}

          {hasTextContent && (
            <div className={`text ${isOutgoing ? 'out' : 'in'} ${isLongText && !expanded ? 'clamped' : ''}`}>
              <div className={`message-content ${expanded ? 'show-less' : ''}`} dangerouslySetInnerHTML={{ __html: contentWithLinks || '' }} />
              {isLongText && (
                <div className={'read-more-btn'} onClick={() => setExpanded((prevExpanded) => !prevExpanded)}>
                  {expanded ? t('SHOW_LESS') : t('READ_MORE')}
                </div>
              )}
              {/* Render timestamp inside text only when there are no attachments and no URL preview */}
              {!hasAttachments && !msg?.url_preview && renderTimestamp()}
            </div>
          )}

          {/* URL Preview - Show only for first URL if multiple exist */}
          {msg?.url_preview && (
            <div className="message-url-preview">
              <URLPreview metadata={msg.url_preview} className="in-message" />
            </div>
          )}

          {hasAttachments &&
            (() => {
              // Separate media and documents using helper function
              const mediaAttachments = msg.attachments.filter((att) => isMediaAttachment(att))
              const docAttachments = msg.attachments.filter((att) => !isMediaAttachment(att))

              // Separate media attachments into those with captions and those without
              const mediaWithCaptions = mediaAttachments.filter((att) => att.caption && att.caption.trim())
              const mediaWithoutCaptions = mediaAttachments.filter((att) => !att.caption || !att.caption.trim())

              const mediaWithCaptionsCount = mediaWithCaptions.length
              const mediaWithoutCaptionsCount = mediaWithoutCaptions.length
              // If ANY media has a caption, render ALL media individually (not in grid)
              const hasAnyCaption = mediaWithCaptionsCount > 0
              const showMediaGrid = mediaWithoutCaptionsCount > 0 && !hasAnyCaption

              // Helper function to render a single media attachment
              const renderSingleMediaAttachment = (att, originalIdx, showCaption = false) => {
                const isPending = att.pending || msg.pending
                const hasCaption = att.caption && att.caption.trim()
                const isExpanded = captionExpanded[originalIdx] || false
                const isLongCaption = hasCaption && att.caption.length > MAX_PREVIEW_CHARS
                const previewCaption = isLongCaption && !isExpanded ? `${att.caption.slice(0, MAX_PREVIEW_CHARS).trim()}...` : att.caption
                const captionWithLinks = hasCaption ? convertLinksToAnchors(previewCaption) : ''

                let mediaNode = null
                const attachmentType = getAttachmentType(att)

                if (attachmentType === 'image') {
                  mediaNode = (
                    <div className="attachment-media-container">
                      <img src={att.attachment_url || att.media_url || att.url} alt={att.file_name || att.name} className="attachment-media" onClick={() => !isPending && onAttachmentClick && onAttachmentClick(msg, att, originalIdx)} onContextMenu={(e) => e.preventDefault()} draggable={false} loading="eager" style={{ opacity: isPending ? 0.7 : 1, transition: 'opacity 0.2s ease-in-out' }} />
                      {isPending && (
                        <div className="attachment-upload-spinner">
                          <i className="pi pi-spin pi-spinner" />
                        </div>
                      )}
                    </div>
                  )
                } else if (attachmentType === 'video') {
                  mediaNode = (
                    <div className="video-wrapper">
                      <video src={att.attachment_url || att.media_url || att.url} className="attachment-media" onClick={() => !isPending && onAttachmentClick && onAttachmentClick(msg, att, originalIdx)} onContextMenu={(e) => e.preventDefault()} controlsList="nodownload" style={{ opacity: isPending ? 0.7 : 1, transition: 'opacity 0.2s ease-in-out' }} />
                      {isPending && (
                        <div className="attachment-upload-spinner">
                          <i className="pi pi-spin pi-spinner" />
                        </div>
                      )}
                      {!isPending && (
                        <div className="play-pause-btn" onClick={() => onAttachmentClick && onAttachmentClick(msg, att, originalIdx)}>
                          <span className="pause-icon">▶</span>
                        </div>
                      )}
                    </div>
                  )
                }

                return (
                  <div key={`media-with-caption-${originalIdx}`} className="attachment-item attachment-item-individual">
                    {mediaNode}
                    {showCaption && hasCaption && (
                      <div className="attachment-caption">
                        <span className={`message-content ${isExpanded ? 'show-less' : ''}`} dangerouslySetInnerHTML={{ __html: captionWithLinks || '' }} />
                        {isLongCaption && (
                          <div className="read-more-btn" onClick={() => setCaptionExpanded((prev) => ({ ...prev, [originalIdx]: !prev[originalIdx] }))}>
                            {isExpanded ? t('SHOW_LESS') : t('READ_MORE')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <div className={`attachments ${shouldOverlayTimestamp ? 'media-only' : ''}`}>
                  {/* If ANY media has a caption, render ALL media individually */}
                  {hasAnyCaption ? (
                    mediaAttachments.map((att) => {
                      const originalIdx = msg.attachments.indexOf(att)
                      const hasCaption = att.caption && att.caption.trim()

                      return renderSingleMediaAttachment(att, originalIdx, hasCaption)
                    })
                  ) : (
                    <>
                      {/* Media Grid Layout - WhatsApp Style (only when NO media has captions) */}
                      {showMediaGrid && (
                        <div className={`media-grid media-grid-${Math.min(mediaWithoutCaptionsCount, 5)}`}>
                          {mediaWithoutCaptions.slice(0, 4).map((att, idx) => {
                            const originalIdx = msg.attachments.indexOf(att)
                            const isMedia = isMediaAttachment(att)
                            const attachmentType = getAttachmentType(att)
                            const showOverlay = shouldOverlayTimestamp && isMedia && idx === mediaWithoutCaptions.length - 1
                            const isLastInGrid = idx === 3 && mediaWithoutCaptionsCount > 4
                            const remainingCount = mediaWithoutCaptionsCount - 4

                            const renderAttachment = () => {
                              const isPending = att.pending || msg.pending

                              if (attachmentType === 'image') {
                                return (
                                  <div className="attachment-media-container">
                                    <img src={att.attachment_url || att.media_url || att.url} alt={att.file_name || att.name} className="attachment-media" onClick={() => !isPending && onAttachmentClick && onAttachmentClick(msg, att, originalIdx)} onContextMenu={(e) => e.preventDefault()} draggable={false} loading="eager" style={{ opacity: isPending ? 0.7 : 1, transition: 'opacity 0.2s ease-in-out' }} />
                                    {isPending && (
                                      <div className="attachment-upload-spinner">
                                        <i className="pi pi-spin pi-spinner" />
                                      </div>
                                    )}
                                  </div>
                                )
                              }

                              if (attachmentType === 'video') {
                                return (
                                  <div className="video-wrapper">
                                    <video src={att.attachment_url || att.media_url || att.url} className="attachment-media" onClick={() => !isPending && onAttachmentClick && onAttachmentClick(msg, att, originalIdx)} onContextMenu={(e) => e.preventDefault()} controlsList="nodownload" style={{ opacity: isPending ? 0.7 : 1, transition: 'opacity 0.2s ease-in-out' }} />
                                    {isPending && (
                                      <div className="attachment-upload-spinner">
                                        <i className="pi pi-spin pi-spinner" />
                                      </div>
                                    )}
                                    {!isPending && (
                                      <div className="play-pause-btn" onClick={() => onAttachmentClick && onAttachmentClick(msg, att, originalIdx)}>
                                        <span className="pause-icon">▶</span>
                                      </div>
                                    )}
                                  </div>
                                )
                              }

                              return null
                            }

                            return (
                              <div
                                key={originalIdx}
                                className={`media-grid-item ${showOverlay ? 'has-overlay' : ''} ${isLastInGrid ? 'has-remaining' : ''}`}
                                onClick={() => {
                                  if (isLastInGrid && onAttachmentClick && !(att.pending || msg.pending)) {
                                    onAttachmentClick(msg, mediaWithoutCaptions[4], msg.attachments.indexOf(mediaWithoutCaptions[4]))
                                  }
                                }}
                              >
                                <div className="attachment-media-wrapper">
                                  {renderAttachment()}
                                  {isLastInGrid && <div className="remaining-count-overlay">+{remainingCount}</div>}
                                  {showOverlay && !isLastInGrid && renderTimestamp('overlay')}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {/* Document attachments (non-media) */}
                  {docAttachments.map((att, idx) => {
                    const originalIdx = msg.attachments.indexOf(att)
                    const isExpanded = captionExpanded[originalIdx] || false
                    const isLongCaption = att.caption && att.caption.length > MAX_PREVIEW_CHARS
                    const previewCaption = isLongCaption && !isExpanded ? `${att.caption.slice(0, MAX_PREVIEW_CHARS).trim()}...` : att.caption

                    return (
                      <div key={`doc-${originalIdx}`} className="attachment-item">
                        <div className="attachment-doc" onClick={() => !(att.pending || msg.pending) && onAttachmentClick && onAttachmentClick(msg, att, originalIdx)} onContextMenu={(e) => e.preventDefault()}>
                          <i className="pi pi-file" />
                          <span className="document-name">{att.file_name}</span>
                          {(att.pending || msg.pending) && (
                            <div className="attachment-upload-spinner">
                              <i className="pi pi-spin pi-spinner" />
                            </div>
                          )}
                        </div>
                        {att.caption && (
                          <div className="attachment-caption">
                            <span className={`message-content ${isExpanded ? 'show-less' : ''}`} dangerouslySetInnerHTML={{ __html: convertLinksToAnchors(previewCaption) || '' }} />
                            {isLongCaption && (
                              <div className="read-more-btn" onClick={() => setCaptionExpanded((prev) => ({ ...prev, [originalIdx]: !prev[originalIdx] }))}>
                                {isExpanded ? t('SHOW_LESS') : t('READ_MORE')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })()}

          {/* Timestamp placement */}
          {/* Show timestamp if:
              - there are attachments AND overlay is not showing (grid without captions), OR
              - there is a URL preview (time should appear below URL metadata) */}
          <div className="message-timestamp-wrapper">{((hasAttachments && !shouldOverlayTimestamp) || (!hasAttachments && msg?.url_preview)) && renderTimestamp(hasTextContent ? 'caption' : '')}</div>
        </div>
      </div>
    )
  },
  (prev, next) => prev.isOutgoing === next.isOutgoing && prev.msg.id === next.msg.id && prev.msg.content === next.msg.content && prev.msg.timestamp === next.msg.timestamp && prev.msg?.user_info?.users_flag_url === next.msg?.user_info?.users_flag_url && prev.msg?.user_info?.first_name === next.msg?.user_info?.first_name && prev.msg?.user_info?.last_name === next.msg?.user_info?.last_name && prev.msg?.edited_at === next.msg?.edited_at && prev.msg?.reply_to_id === next.msg?.reply_to_id && prev.msg?.repliedMessage === next.msg?.repliedMessage && prev.onAttachmentClick === next.onAttachmentClick && prev.onReplyClick === next.onReplyClick && prev.menuNode === next.menuNode
)

PersonalChatMessage.displayName = 'PersonalChatMessage'

export default PersonalChatMessage
